/**
 * Express Payment Methods Context Provider using v2 Architecture
 * Manages express payment methods (Apple Pay, Google Pay, PayPal, Klarna)
 *
 * Payment methods are resolved from:
 *   1. paymentSetupConfig prop (from __TGD_STEP_CONFIG__ or useStepConfig)
 *   2. API fallback (GET /api/v1/payment-methods) for legacy integrations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { TagadaError, TagadaErrorCode } from '../../core/errors';

import type { PaymentSetupConfig } from '../../core/funnelClient';
import { CheckoutData } from '../../core/resources/checkout';
import {
  Address,
  ExpressPaymentMethodsResource,
  PaymentMethod,
} from '../../core/resources/expressPaymentMethods';
import { getGlobalApiClient } from '../hooks/useApiQuery';
import { useShippingRatesQuery } from '../hooks/useShippingRatesQuery';

type ExpressOrderLineItem = { label: string; amount: string };
type ExpressShippingMethod = { label: string; amount: string; identifier: string; detail: string };

interface ExpressPaymentMethodsContextType {
  // Payment methods
  paymentMethods: PaymentMethod[] | undefined;
  applePayPaymentMethod?: PaymentMethod;
  googlePayPaymentMethod?: PaymentMethod;
  paypalPaymentMethod?: PaymentMethod;
  klarnaPaymentMethod?: PaymentMethod;
  stripeExpressPaymentMethod?: PaymentMethod;

  // Express payment availability
  availableExpressPaymentMethodIds: string[];
  setAvailableExpressPaymentMethodIds: (value: string[]) => void;
  handleAddExpressId: (id: string) => void;

  // Order data for express checkout
  shippingMethods: ExpressShippingMethod[];
  lineItems: ExpressOrderLineItem[];
  reComputeOrderSummary: () => Promise<
    | {
        lineItems: ExpressOrderLineItem[];
        total: { label: string; amount: string };
        shippingMethods: ExpressShippingMethod[];
        selectedShippingRateId?: string;
      }
    | undefined
  >;

  // Checkout updates
  updateCheckoutSessionValues: (input: {
    data: { shippingAddress: Address; billingAddress?: Address | null };
  }) => Promise<void>;
  updateCustomerEmail: (input: { data: { email: string } }) => Promise<void>;

  // State
  loading?: boolean;
  error: TagadaError | null;
  setError: (error: string | null) => void;
}

interface ExpressPaymentMethodsProviderProps {
  children: ReactNode;
  customerId?: string;
  checkout?: CheckoutData;
  /** Step-level payment config from __TGD_STEP_CONFIG__ / useStepConfig(). */
  paymentSetupConfig?: PaymentSetupConfig;
}

export const ExpressPaymentMethodsContext = createContext<ExpressPaymentMethodsContextType | undefined>(
  undefined,
);

/**
 * Synthesize PaymentMethod[] from paymentSetupConfig entries.
 * Converts express_checkout:processorId entries into the shape the provider expects.
 */
function paymentMethodsFromSetupConfig(config: PaymentSetupConfig): PaymentMethod[] {
  const methods: PaymentMethod[] = [];

  for (const [key, entry] of Object.entries(config)) {
    if (!entry.enabled) continue;

    // express_checkout:processorId entries → synthesize a stripe_apm-style method
    if (key.startsWith('express_checkout:') && entry.methods) {
      methods.push({
        id: key,
        type: 'stripe_apm',
        title: entry.label || 'Express Checkout',
        iconUrl: entry.logoUrl || '',
        default: false,
        settings: {
          publishableKey: entry.publishableKey,
          processors: [{
            processorId: entry.processorId,
            methods: entry.methods,
          }],
        },
      });
      continue;
    }

    // Individual express methods (apple_pay, google_pay, etc.)
    // Match by: explicit express flag, type, method, provider, or key name
    const resolvedMethod = entry.method || entry.provider || key;
    const isExpressMethod = entry.express
      || entry.type === 'apple_pay' || entry.type === 'google_pay'
      || resolvedMethod === 'apple_pay' || resolvedMethod === 'google_pay';
    if (isExpressMethod) {
      methods.push({
        id: key,
        type: entry.type === 'apple_pay' || entry.type === 'google_pay' ? entry.type : resolvedMethod,
        title: entry.label || key,
        iconUrl: entry.logoUrl || '',
        default: false,
        settings: { processorId: entry.processorId, publishableKey: entry.publishableKey },
      });
    }
  }

  return methods;
}

export const ExpressPaymentMethodsProvider: React.FC<ExpressPaymentMethodsProviderProps> = ({
  children,
  customerId,
  checkout,
  paymentSetupConfig,
}) => {
  const queryClient = useQueryClient();
  const [availableExpressPaymentMethodIds, setAvailableExpressPaymentMethodIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const checkoutSessionId = checkout?.checkoutSession?.id;

  // If paymentSetupConfig is provided AND contains at least one enabled method, derive from it.
  // Missing, empty `{}`, or all-disabled configs fall through to the API fallback.
  const configDerivedMethods = useMemo(
    () => (paymentSetupConfig && Object.values(paymentSetupConfig).some((v: any) => v?.enabled === true))
      ? paymentMethodsFromSetupConfig(paymentSetupConfig)
      : undefined,
    [paymentSetupConfig],
  );

  // Create express payment methods resource client
  const expressPaymentResource = useMemo(() => {
    try {
      return new ExpressPaymentMethodsResource(getGlobalApiClient());
    } catch (error) {
      throw new Error(
        'Failed to initialize express payment methods resource: ' +
          (error instanceof Error ? error.message : 'Unknown error'),
      );
    }
  }, []);

  // Only fetch from API when paymentSetupConfig is NOT provided
  const { data: apiPaymentMethods, isLoading: isLoadingPaymentMethods } = useQuery<PaymentMethod[]>({
    queryKey: ['payment-methods', checkoutSessionId],
    queryFn: () => expressPaymentResource.getPaymentMethods(checkoutSessionId!),
    enabled: !!checkoutSessionId && !configDerivedMethods,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const paymentMethods = configDerivedMethods ?? apiPaymentMethods;

  // Use v2 shipping rates hook
  const { shippingRates, refetch: refetchRates } = useShippingRatesQuery({ checkout });

  // Get order summary from checkout data
  const orderSummary = checkout?.summary;

  const handleAddExpressId = useCallback((id: string) => {
    setAvailableExpressPaymentMethodIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const minorUnitsToCurrencyString = useCallback(
    (amountMinor: number | undefined, currency: string | undefined) => {
      // Fail safely - don't allow invalid data to become '0.00'
      if (amountMinor === undefined || amountMinor === null || !currency) {
        throw new Error(`Invalid currency data: amountMinor=${amountMinor}, currency=${currency}`);
      }
      // 0 is a valid amount (e.g., free shipping)
      return (amountMinor / 100).toFixed(2);
    },
    [],
  );

  // Convert shipping rates to express shipping methods
  const shippingMethods: ExpressShippingMethod[] = useMemo(
    () =>
      (shippingRates || []).map((rate) => ({
        label: rate.shippingRateName,
        amount: minorUnitsToCurrencyString(rate.amount, rate.currency),
        identifier: rate.id,
        detail: rate.description || '',
      })),
    [shippingRates, minorUnitsToCurrencyString],
  );

  // Convert order summary to line items
  const lineItems: ExpressOrderLineItem[] = useMemo(
    () => [
      {
        label: 'Subtotal',
        amount: minorUnitsToCurrencyString(orderSummary?.subtotalAdjustedAmount, orderSummary?.currency),
      },
      {
        label: 'Shipping',
        amount: minorUnitsToCurrencyString(orderSummary?.shippingCost ?? 0, orderSummary?.currency),
      },
      {
        label: 'Tax',
        amount: minorUnitsToCurrencyString(orderSummary?.totalTaxAmount, orderSummary?.currency),
      },
    ],
    [orderSummary, minorUnitsToCurrencyString],
  );

  // Update checkout session address mutation
  const updateAddressMutation = useMutation({
    mutationFn: (data: { shippingAddress: Address; billingAddress?: Address | null }) =>
      expressPaymentResource.updateCheckoutSessionAddress(checkoutSessionId!, { data }),
    onSuccess: async () => {
      // Invalidate and wait for checkout query to refetch
      await queryClient.invalidateQueries({ queryKey: ['checkout'] });
      // Small delay to ensure parent's query has time to refetch
      await new Promise((resolve) => setTimeout(resolve, 300));
    },
  });

  // Update customer email mutation
  const updateEmailMutation = useMutation({
    mutationFn: (data: { email: string }) =>
      expressPaymentResource.updateCustomerEmail(customerId!, { data }),
  });

  // Recompute order summary (refetch after updates)
  // Refetches checkout and shipping rates to get fresh data
  const reComputeOrderSummary = useCallback(async () => {
    try {
      // Refetch checkout to get latest data
      await queryClient.refetchQueries({ queryKey: ['checkout'] });
      await refetchRates();
      // Wait a bit for queries to settle
      await new Promise((resolve) => setTimeout(resolve, 100));
      // Get fresh data from cache
      const queryCache = queryClient.getQueryCache();
      const checkoutQueries = queryCache.findAll({ queryKey: ['checkout'] });
      // Try to find the checkout that matches our current session ID
      const currentSessionId = checkout?.checkoutSession?.id;
      const matchingCheckout = checkoutQueries.find(
        (query) =>
          query.state.data && (query.state.data as CheckoutData)?.checkoutSession?.id === currentSessionId,
      );
      const freshCheckout = matchingCheckout
        ? (matchingCheckout.state.data as CheckoutData)
        : checkoutQueries.length > 0
          ? (checkoutQueries[0].state.data as CheckoutData)
          : checkout;
      const freshSummary = freshCheckout?.summary || checkout?.summary;

      if (!freshSummary || !shippingRates) {
        console.error('[ExpressProvider] Missing data:', {
          hasSummary: !!freshSummary,
          hasShippingRates: !!shippingRates,
          checkout: freshCheckout || checkout,
        });
        return undefined;
      }

      const recomputedLineItems: ExpressOrderLineItem[] = [
        {
          label: 'Subtotal',
          amount: minorUnitsToCurrencyString(freshSummary.subtotalAdjustedAmount, freshSummary.currency),
        },
        {
          label: 'Shipping',
          amount: minorUnitsToCurrencyString(freshSummary.shippingCost ?? 0, freshSummary.currency),
        },
        {
          label: 'Tax',
          amount: minorUnitsToCurrencyString(freshSummary.totalTaxAmount, freshSummary.currency),
        },
      ];

      const total = {
        label: 'Order Total',
        amount: minorUnitsToCurrencyString(freshSummary.totalAdjustedAmount, freshSummary.currency),
      };

      const recomputedShippingMethods: ExpressShippingMethod[] = (shippingRates || []).map((rate) => ({
        label: rate.shippingRateName,
        amount: minorUnitsToCurrencyString(rate.amount, rate.currency),
        identifier: rate.id,
        detail: rate.description || '',
      }));

      // Get selected shipping rate ID from fresh checkout
      const selectedShippingRateId = freshCheckout?.checkoutSession?.shippingRate?.id;

      return {
        lineItems: recomputedLineItems,
        total,
        shippingMethods: recomputedShippingMethods,
        selectedShippingRateId,
      };
    } catch (e) {
      console.error('Error recomputing order summary:', e);
      return undefined;
    }
  }, [refetchRates, shippingRates, minorUnitsToCurrencyString, checkout, queryClient]);

  // Update checkout session values
  const updateCheckoutSessionValues = useCallback(
    async (input: { data: { shippingAddress: Address; billingAddress?: Address | null } }) => {
      await updateAddressMutation.mutateAsync(input.data);
    },
    [updateAddressMutation],
  );

  // Update customer email
  const updateCustomerEmail = useCallback(
    async (input: { data: { email: string } }) => {
      if (!customerId) return;
      await updateEmailMutation.mutateAsync(input.data);
    },
    [customerId, updateEmailMutation],
  );

  // Check if Apple Pay is available — load SDK on demand then re-check reactively
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(
    () => typeof window !== 'undefined' && typeof window.ApplePaySession !== 'undefined',
  );
  const [isApplePaySdkLoading, setIsApplePaySdkLoading] = useState(false);

  const hasApplePayConfig = paymentMethods?.some((p) => p.type === 'apple_pay');

  useEffect(() => {
    if (isApplePayAvailable || !hasApplePayConfig) return;

    const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

    const onSdkReady = () => {
      if (typeof window.ApplePaySession !== 'undefined') {
        setIsApplePayAvailable(true);
      }
      setIsApplePaySdkLoading(false);
    };

    // Check if script is already in DOM (may still be loading)
    const existing = document.querySelector(`script[src="${APPLE_PAY_SDK_URL}"]`) as HTMLScriptElement | null;
    if (existing) {
      setIsApplePaySdkLoading(true);
      // Script may already be loaded or still loading
      if (typeof window.ApplePaySession !== 'undefined') {
        onSdkReady();
      } else {
        existing.addEventListener('load', onSdkReady);
        // Fallback timeout in case we missed the load event
        const timer = setTimeout(onSdkReady, 2000);
        return () => {
          existing.removeEventListener('load', onSdkReady);
          clearTimeout(timer);
        };
      }
      return;
    }

    setIsApplePaySdkLoading(true);
    const script = document.createElement('script');
    script.src = APPLE_PAY_SDK_URL;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = onSdkReady;
    script.onerror = () => setIsApplePaySdkLoading(false);
    document.head.appendChild(script);
  }, [isApplePayAvailable, hasApplePayConfig]);

  // Identify specific payment method types
  const applePayPaymentMethod = useMemo(
    () => {
      return isApplePayAvailable ? paymentMethods?.find((p) => p.type === 'apple_pay') : undefined;
    },
    [paymentMethods, isApplePayAvailable],
  );
  const googlePayPaymentMethod = useMemo(
    () => paymentMethods?.find((p) => p.type === 'google_pay'),
    [paymentMethods],
  );
  const paypalPaymentMethod = useMemo(
    () => paymentMethods?.find((p) => p.type === 'paypal'),
    [paymentMethods],
  );
  const klarnaPaymentMethod = useMemo(
    () => paymentMethods?.find((p) => p.type === 'klarna'),
    [paymentMethods],
  );

  // Find a stripe_apm or tagadapay_apm integration with any express method enabled in any processor group.
  // Backward compatible: existing configs with only apple_pay/google_pay continue to match.
  const stripeExpressPaymentMethod = useMemo(
    () => {
      const EXPRESS_METHOD_KEYS = ['apple_pay', 'google_pay', 'paypal', 'link', 'klarna_express'];
      return paymentMethods?.find(
        (p) =>
          (p.type === 'stripe_apm' || p.type === 'tagadapay_apm') &&
          (p.settings as any)?.processors?.some((g: any) =>
            EXPRESS_METHOD_KEYS.some((key) => g?.methods?.[key]?.enabled === true),
          ),
      );
    },
    [paymentMethods],
  );

  const loading = !paymentMethods || (!configDerivedMethods && isLoadingPaymentMethods) || isApplePaySdkLoading;

  const tagadaError = useMemo(
    () => error ? new TagadaError(error, { code: TagadaErrorCode.PAYMENT_FAILED }) : null,
    [error],
  );

  const contextValue: ExpressPaymentMethodsContextType = {
    paymentMethods,
    availableExpressPaymentMethodIds,
    setAvailableExpressPaymentMethodIds,
    applePayPaymentMethod,
    googlePayPaymentMethod,
    paypalPaymentMethod,
    klarnaPaymentMethod,
    stripeExpressPaymentMethod,
    shippingMethods,
    lineItems,
    reComputeOrderSummary,
    loading,
    handleAddExpressId,
    updateCheckoutSessionValues,
    updateCustomerEmail,
    error: tagadaError,
    setError,
  };

  const hasAnyEnabled = Boolean(
    applePayPaymentMethod || googlePayPaymentMethod || paypalPaymentMethod || klarnaPaymentMethod || stripeExpressPaymentMethod,
  );

  return (
    <ExpressPaymentMethodsContext.Provider value={contextValue}>
      {hasAnyEnabled ? <>{children}</> : <></>}
    </ExpressPaymentMethodsContext.Provider>
  );
};

// useExpressPaymentMethods hook is now in dedicated hooks/useExpressPaymentMethods.ts file

// Re-export types
export type { ExpressPaymentMethodsContextType, ExpressPaymentMethodsProviderProps };

